import { FC, useMemo } from 'react';

import ColumnsIcon from '@/assets/svg/ui-elements/columns-3-cog.svg';

import { IconButton } from '../Button';
import Select from '../Select';
import type { SelectOption } from '../Select/types';
import type { ColumnsPopoverProps } from './types';

const GROUPS = {
  always: { label: 'Always visible' },
  optional: { label: 'Optional columns' },
};

const POPUP_WIDTH = 300;

/** Show / hide table columns from a toolbar icon button. Built on the Select multi-picker. */
const ColumnsPopover: FC<ColumnsPopoverProps> = ({
  columns,
  visible,
  onVisibleChange,
  ariaLabel = 'Columns',
  open,
  onOpenChange,
}) => {
  const options = useMemo<SelectOption[]>(
    () =>
      columns.map((column) => ({
        value: column.key,
        label: column.label,
        group: column.locked ? 'always' : 'optional',
        disabled: column.locked,
      })),
    [columns],
  );

  const value = columns
    .filter((column) => column.locked || visible.includes(column.key))
    .map((column) => column.key);

  const handleChange = (next: string[]) =>
    onVisibleChange(
      columns
        .filter((column) => column.locked || next.includes(column.key))
        .map((column) => column.key),
    );

  return (
    <Select
      multiple
      showSearch
      showSelectAll
      ariaLabel={ariaLabel}
      options={options}
      groups={GROUPS}
      value={value}
      onChange={handleChange}
      popupWidth={POPUP_WIDTH}
      align="end"
      open={open}
      onOpenChange={onOpenChange}
      trigger={
        <IconButton variant="outline" color="tertiary" size="xs">
          <ColumnsIcon />
        </IconButton>
      }
    />
  );
};

ColumnsPopover.displayName = 'ColumnsPopover';

export default ColumnsPopover;
